import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from './useTheme';
import { ThemeSwitch } from './ThemeSwitch';
import { NAV_TABS, useNav } from './nav';
import s from './Shell.module.css';

// Real app frame: brand bar (with the theme switch), the screen content, and the
// fixed bottom nav. The nav only shows when a NavContext is present (i.e. the full
// app); rendering a screen standalone just shows the framed content.

const ICONS: Record<string, ReactNode> = {
  home: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V21h14V9.5" /></svg>,
  vote: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16M4 12h16M4 18h10" /><circle cx="19" cy="18" r="2.4" /></svg>,
  results: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 4h10v4a5 5 0 0 1-10 0z" /><path d="M5 4H3v2a3 3 0 0 0 3 3M19 4h2v2a3 3 0 0 1-3 3M9 20h6M12 13v7" /></svg>,
  standings: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 21V10M12 21V4M19 21v-7" /></svg>,
  chat: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a8 8 0 0 1-11.5 7.2L4 20l.9-4.5A8 8 0 1 1 21 12z" /></svg>,
};

export function Shell({ children }: { children: ReactNode }) {
  const { mode } = useTheme();
  const nav = useNav();
  const navigate = useNavigate();
  return (
    <div className="tp-root" data-mode={mode}>
      <div className={s.root}>
        <div className={s.col}>
          <div className={s.topbar}>
            <button className={s.wm} onClick={() => navigate('/leagues')} aria-label="Switch league" title="Your leagues">
              <span className={s.mk}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M9 18V6l10-2v12" /><circle cx="6" cy="18" r="3" /><circle cx="16" cy="16" r="3" /></svg></span>
              TPIAP
            </button>
            {/* Desktop navigation lives in the top bar (the bottom bar is hidden ≥900px). */}
            {nav ? (
              <nav className={s.toptabs}>
                {NAV_TABS.map((t) => (
                  <button key={t.id} className={nav.current === t.id ? s.tabon : ''} onClick={() => nav.go(t.id)}>
                    {ICONS[t.id]}<span>{t.label}</span>
                  </button>
                ))}
              </nav>
            ) : null}
            <ThemeSwitch />
          </div>
          <div className={s.content}>{children}</div>
        </div>
        {nav ? (
          <div className={s.nav}>
            {NAV_TABS.map((t) => (
              <button key={t.id} className={nav.current === t.id ? s.on : ''} onClick={() => nav.go(t.id)} aria-label={t.label}>
                {ICONS[t.id]}<span>{t.label}</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
